import { uploadFile } from "@/features/page/services/page-service.ts";
import { getFileUploadSizeLimit } from "@/lib/config.ts";
import { formatBytes } from "@/lib";
import { notifications } from "@mantine/notifications";
import i18n from "@/i18n.ts";

export interface UploadedIcon {
  /** Storage path the public site resolves via toPublicAssetUrl(). */
  src: string;
  /** File name used as alt text fallback. */
  alt: string;
  /** Attachment id — kept so a future cleanup pass can detach orphaned files. */
  attachmentId: string;
  /** Source-file natural width in px (used to set aspect-ratio on inline imgs). */
  naturalWidth: number | null;
  /** Source-file natural height in px. */
  naturalHeight: number | null;
}

/**
 * Read the natural dimensions of an image File without uploading it. Uses
 * an off-DOM <img> element + objectURL so the file never has to round-trip
 * through the server first. Falls back to null on any decode error.
 */
function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims =
        img.naturalWidth > 0 && img.naturalHeight > 0
          ? { width: img.naturalWidth, height: img.naturalHeight }
          : null;
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

// Log a step into both the browser console (for devtools tracing) and a
// short-lived toast so the user can see progress without opening devtools.
function trace(label: string, payload?: unknown) {
  // eslint-disable-next-line no-console
  console.log(`[icon-upload] ${label}`, payload ?? "");
}

/**
 * Open a native file picker, upload selected images to the same `/files/upload`
 * endpoint used for inline images, return their public src paths. Heavily
 * instrumented (notifications + console.log) so we can pinpoint exactly
 * where icon upload fails in production.
 */
export async function pickAndUploadIcons(
  pageId: string,
  options: { multiple?: boolean } = {},
): Promise<UploadedIcon[]> {
  trace("opened picker", { pageId, multiple: !!options.multiple });
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = options.multiple ?? false;
    input.style.display = "none";
    document.body.appendChild(input);

    input.onchange = async () => {
      const files = Array.from(input.files ?? []);
      input.remove();
      trace("picked files", files.map((f) => ({ name: f.name, size: f.size, type: f.type })));
      if (files.length === 0) {
        notifications.show({
          color: "yellow",
          message: "No files selected — insert cancelled.",
        });
        resolve([]);
        return;
      }

      const limit = getFileUploadSizeLimit();
      trace("workspace upload limit", limit);
      const valid = files.filter((f) => {
        if (!f.type.startsWith("image/")) {
          notifications.show({
            color: "red",
            message: `${f.name}: not an image (${f.type || "unknown type"}).`,
          });
          return false;
        }
        if (f.size > limit) {
          notifications.show({
            color: "red",
            message: i18n.t("File exceeds the {{limit}} attachment limit", {
              limit: formatBytes(limit),
            }),
          });
          return false;
        }
        return true;
      });

      if (valid.length === 0) {
        resolve([]);
        return;
      }

      const loadingId = notifications.show({
        loading: true,
        autoClose: false,
        withCloseButton: false,
        message: `Uploading ${valid.length} icon${valid.length === 1 ? "" : "s"}…`,
      });

      const out: UploadedIcon[] = [];
      for (const file of valid) {
        trace("uploading", { name: file.name, size: file.size });
        try {
          // Client-side timeout — B2 upload occasionally stalls past
          // 60s; without this the toast spinner would hang forever.
          // 45s covers the 99th percentile of healthy uploads; anything
          // longer is surfaced to the user as a clear error so they can
          // retry instead of staring at a spinner.
          const att = await Promise.race<any>([
            uploadFile(file, pageId),
            new Promise((_resolve, reject) =>
              setTimeout(
                () =>
                  reject(
                    new Error(
                      "Upload timed out after 45s (storage backend slow). Please retry.",
                    ),
                  ),
                45000,
              ),
            ),
          ]);
          trace("upload response", att);
          if (!att?.id || !att?.fileName) {
            throw new Error(
              `Server returned ${JSON.stringify(att)} (expected {id, fileName, …})`,
            );
          }
          // Read source dimensions before pushing so consumers can preserve
          // aspect ratio (inline image uses this for the CSS aspect-ratio
          // property — eliminates layout shift from native pixel height).
          const dims = await readImageDimensions(file);
          out.push({
            src: `/api/files/${att.id}/${att.fileName}`,
            alt: file.name,
            attachmentId: att.id,
            naturalWidth: dims?.width ?? null,
            naturalHeight: dims?.height ?? null,
          });
        } catch (err: any) {
          // Axios errors expose status/code/data; print them so the user
          // can copy the exact failure into a bug report.
          const status = err?.response?.status;
          const serverMsg = err?.response?.data?.message;
          const detail = serverMsg
            ? `${status ?? "?"}: ${serverMsg}`
            : err?.message ?? String(err);
          trace("upload error", { name: file.name, status, serverMsg, err });
          notifications.show({
            color: "red",
            title: `Icon upload failed: ${file.name}`,
            message: detail,
            autoClose: 10000,
          });
        }
      }

      notifications.hide(loadingId);
      if (out.length > 0) {
        notifications.show({
          color: "green",
          message: `Uploaded ${out.length}/${valid.length} icon${out.length === 1 ? "" : "s"}.`,
          autoClose: 3000,
        });
      }
      trace("done", { uploaded: out.length, attempted: valid.length });
      resolve(out);
    };

    input.oncancel = () => {
      input.remove();
      trace("picker cancelled");
      resolve([]);
    };

    input.click();
  });
}
